import { DateInput, FormField, TaxIdInput, isolate } from '@batiflow/ui';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Input,
  MessageBar,
  MessageBarBody,
  Radio,
  RadioGroup,
  Spinner,
  __css,
  tokens,
} from '@fluentui/react-components';
import { useState, type SyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { DirectoryPicker } from '@/features/directory/directory-picker';
import { todayInTunis } from '@/features/admin/fiscal/model';
import { useNotify } from '@/shared/hooks/use-notify';
import { isStepUpRequired } from '../model';
import { useInvitePartner, usePartnerLookup } from './api';
import {
  withoutKey,
  INITIAL_SHARE_VALUES,
  RIGHTS_ORDER,
  buildPartnerInvite,
  errorCodeOf,
  isRights,
  sharingErrorMessage,
  type ShareErrors,
  type ShareValues,
} from './model';
import { ScopePicker } from './scope-picker';

const useStyles = __css({"form":{"mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62","Belr9w4":"f1noh358"},"intro":{"Bo78w4l":0,"Cgygva":0,"yil37a":"f8ntp42"},"note":{"Bo78w4l":0,"Cgygva":0,"yil37a":"f8ntp42","Be2twd7":"fkhj508","sj55zd":"fkfq4zb"},"cap":{"D305fb":"fd1ux1j"}});

export interface ShareDialogProps {
  spaceId: string;
  /** The host organisation (the lookup of a tax id is asked in its name). */
  hostOrgId: string;
  open: boolean;
  onClose: () => void;
}

function ShareForm({ spaceId, hostOrgId, onClose }: Omit<ShareDialogProps, 'open'>) {
  const { t } = useTranslation('spaces');
  const { t: tCommon } = useTranslation('common');
  const styles = useStyles();
  const notify = useNotify();
  const invite = useInvitePartner(spaceId);

  const [values, setValues] = useState<ShareValues>(INITIAL_SHARE_VALUES);
  const [errors, setErrors] = useState<ShareErrors>({});
  const [failure, setFailure] = useState<string | null>(null);
  const [picking, setPicking] = useState(false);

  const lookup = usePartnerLookup(hostOrgId, values.taxId);
  const foundName = lookup.isSuccess ? lookup.data.name : null;
  const notFound = lookup.isError && errorCodeOf(lookup.error) === 'NOT_FOUND';

  const change = (patch: Partial<ShareValues>) => {
    setValues((current) => ({ ...current, ...patch }));
  };

  const submit = async (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (invite.isPending) return;
    setFailure(null);
    const result = buildPartnerInvite(
      { ...values, organizationName: values.directoryId ? values.organizationName : foundName },
      t,
      new Date(),
    );
    if (!result.ok) {
      setErrors(result.errors);
      return;
    }
    setErrors({});
    try {
      await invite.mutateAsync({ spaceId, data: result.data });
      notify({
        intent: 'success',
        message: t('sharing.share.sent', { name: isolate(foundName ?? '') }),
      });
      onClose();
    } catch (error) {
      // STEP_UP_REQUIRED: the app's step-up dialog is open now and replays this invitation.
      if (isStepUpRequired(error)) return;
      if (values.directoryId && errorCodeOf(error) === 'NOT_FOUND') {
        // The office left the directory (or stopped) since the list was shown.
        change({ directoryId: null, organizationName: null });
        setFailure(t('sharing.share.directory.gone'));
        return;
      }
      setFailure(
        sharingErrorMessage(t, tCommon, error, { forbidden: 'sharing.share.errors.forbidden' }),
      );
    }
  };

  return (
    <form
      noValidate
      aria-label={t('sharing.share.title')}
      onSubmit={(event) => {
        void submit(event);
      }}
    >
      <DialogBody>
        <DialogTitle>{t('sharing.share.title')}</DialogTitle>
        <DialogContent>
          <div className={styles.form}>
            <p className={styles.intro}>{t('sharing.share.intro')}</p>
            {values.directoryId ? (
              <MessageBar intent="success" layout="multiline">
                <MessageBarBody>
                  {t('sharing.share.directory.chosen', {
                    name: isolate(values.organizationName ?? ''),
                  })}{' '}
                  <Button
                    type="button"
                    appearance="transparent"
                    size="small"
                    onClick={() => {
                      change({ directoryId: null, organizationName: null });
                    }}
                  >
                    {t('sharing.share.directory.change')}
                  </Button>
                </MessageBarBody>
              </MessageBar>
            ) : picking ? (
              <DirectoryPicker
                onPick={(choice) => {
                  change({
                    directoryId: choice.organizationId,
                    organizationName: choice.name,
                    taxId: null,
                  });
                  setErrors((current) => withoutKey(current, 'taxId'));
                  setPicking(false);
                }}
                onCancel={() => {
                  setPicking(false);
                }}
              />
            ) : (
              <>
                {/* Enter checks the tax id (the field does it); it must not send the form yet. */}
                <div
                  role="presentation"
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') event.preventDefault();
                  }}
                >
                  <TaxIdInput
                    required
                    label={t('sharing.share.taxId.label')}
                    hint={t('sharing.share.taxId.hint')}
                    value={values.taxId}
                    error={errors.taxId}
                    onChange={(taxId) => {
                      change({ taxId, organizationName: null });
                      setErrors((current) => withoutKey(current, 'taxId'));
                    }}
                  />
                </div>
                {values.taxId !== null && lookup.isFetching ? (
                  <Spinner size="tiny" label={t('sharing.share.lookup.loading')} />
                ) : null}
                {foundName !== null ? (
                  <MessageBar intent="success" layout="multiline">
                    <MessageBarBody>
                      {t('sharing.share.lookup.found', { name: isolate(foundName) })}
                    </MessageBarBody>
                  </MessageBar>
                ) : null}
                {notFound ? (
                  <MessageBar intent="warning" layout="multiline">
                    <MessageBarBody>{t('sharing.share.lookup.notFound')}</MessageBarBody>
                  </MessageBar>
                ) : null}
                {lookup.isError && !notFound ? (
                  <MessageBar intent="error" layout="multiline">
                    <MessageBarBody>{t('sharing.share.lookup.failed')}</MessageBarBody>
                  </MessageBar>
                ) : null}

                <div>
                  <Button
                    type="button"
                    appearance="secondary"
                    onClick={() => {
                      setPicking(true);
                    }}
                  >
                    {t('sharing.share.directory.open')}
                  </Button>
                </div>
              </>
            )}

            <ScopePicker
              spaceId={spaceId}
              allowWhole
              value={values.scope}
              error={errors.scope ?? null}
              onChange={(scope) => {
                change({ scope });
                setErrors((current) => withoutKey(current, 'scope'));
              }}
            />

            <FormField label={t('sharing.rightsOption.label')}>
              <RadioGroup
                value={values.rights}
                onChange={(_event, data) => {
                  if (isRights(data.value)) change({ rights: data.value });
                }}
              >
                {RIGHTS_ORDER.map((rights) => (
                  <Radio key={rights} value={rights} label={t(`sharing.rightsOption.${rights}`)} />
                ))}
              </RadioGroup>
            </FormField>

            <FormField
              required
              label={t('sharing.share.cap.label')}
              hint={t('sharing.share.cap.hint')}
              error={errors.memberCap}
            >
              <Input
                className={styles.cap}
                type="number"
                inputMode="numeric"
                min={1}
                max={500}
                value={values.memberCap}
                onChange={(_event, data) => {
                  change({ memberCap: data.value });
                  setErrors((current) => withoutKey(current, 'memberCap'));
                }}
              />
            </FormField>

            <DateInput
              label={t('sharing.share.end.label')}
              hint={t('sharing.share.end.hint')}
              value={values.endsOn}
              min={todayInTunis(new Date())}
              error={errors.endsOn}
              onChange={(endsOn) => {
                change({ endsOn });
                setErrors((current) => withoutKey(current, 'endsOn'));
              }}
            />

            <p className={styles.note}>{t('sharing.share.stepUpNote')}</p>
            {failure ? (
              <MessageBar intent="error" layout="multiline">
                <MessageBarBody>{failure}</MessageBarBody>
              </MessageBar>
            ) : null}
          </div>
        </DialogContent>
        <DialogActions>
          <Button type="button" appearance="secondary" onClick={onClose}>
            {tCommon('actions.cancel')}
          </Button>
          <Button type="submit" appearance="primary" disabledFocusable={invite.isPending}>
            {invite.isPending ? t('sharing.share.submitting') : t('sharing.share.submit')}
          </Button>
        </DialogActions>
      </DialogBody>
    </form>
  );
}

/**
 * "Partager avec une organisation": the host finds an organisation by its tax id, confirms its
 * name, chooses what it may reach (the whole space, or folders and channels), the rights, the
 * number of people it may add and an optional end date. The API asks for a recent identity check
 * (step-up); the app's step-up dialog handles it and replays the invitation.
 */
export function ShareDialog({ open, onClose, ...rest }: ShareDialogProps) {
  return (
    <Dialog
      open={open}
      modalType="modal"
      onOpenChange={(_event, data) => {
        if (!data.open) onClose();
      }}
    >
      <DialogSurface>
        <ShareForm {...rest} onClose={onClose} />
      </DialogSurface>
    </Dialog>
  );
}


import "/opt/batiflow/app/apps/web/src/features/spaces/sharing/share-dialog.griffel.css";